import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import AiScoreBadge from "../../components/AiScoreBadge";
import StatusBadge from "../../components/StatusBadge";
import { fmtDate, LOAN_TYPE_LABELS } from "../../lib/constants";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  "sponsor_review","submitted_to_admin","ok_to_apply","docs_requested",
  "application_sent","under_review","approved","denied",
];

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("under_review");
  const [note, setNote] = useState("");
  const [params] = useSearchParams();
  const highlight = params.get("highlight");
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/applications");
      setApps(data);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (highlight) {
      const el = document.getElementById(`app-row-${highlight}`);
      if (el) {
        el.classList.add("highlight-pulse");
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [highlight, apps]);

  const openChange = (a) => { setSelected(a); setStatus(a.status); setNote(""); setOpen(true); };
  const openDetail = (id) => nav(`/admin/applications/${id}`);

  const save = async () => {
    try {
      await api.post(`/admin/applications/${selected.application_id}/status`, { status, note });
      toast.success("Status updated");
      setOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const requestDocs = async (id) => {
    try {
      await api.post(`/admin/applications/${id}/request-docs`);
      toast.success("Documents requested");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Applications</h1>
        <p className="mt-1 text-sm text-slate-500">All applications across all brokers. Click a row to open.</p>
      </div>

      {/* Desktop table */}
      <Card className="hidden overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 text-left">Borrower</th>
                <th className="px-4 py-3 text-left">Broker</th>
                <th className="px-4 py-3 text-left">Property</th>
                <th className="px-4 py-3 text-left">Loan</th>
                <th className="px-4 py-3 text-left">AI</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Source</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="apps-empty">No applications yet.</td></tr>
              )}
              {apps.map((a) => (
                <tr
                  key={a.application_id}
                  id={`app-row-${a.application_id}`}
                  onClick={() => openDetail(a.application_id)}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/50"
                  data-testid={`admin-app-${a.application_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{a.borrower_name}</div>
                    <div className="text-xs text-slate-500">{a.borrower_email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{a.sponsor_name || "—"}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-sm text-slate-600">{a.property_address}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{LOAN_TYPE_LABELS[a.loan_type] || a.loan_type}</td>
                  <td className="px-4 py-3"><AiScoreBadge score={a.ai_score} testId={`admin-score-${a.application_id}`} /></td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} testId={`admin-status-${a.application_id}`} /></td>
                  <td className="px-4 py-3">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                      {a.submitted_by === "sponsor" ? "Broker" : "Borrower"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{fmtDate(a.created_at)}</td>
                  <td className="px-4 py-3 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" onClick={() => openDetail(a.application_id)} className="bg-blue-600 text-white hover:bg-blue-700" data-testid={`admin-open-${a.application_id}`}>Open</Button>
                    <Button size="sm" variant="outline" onClick={() => requestDocs(a.application_id)} data-testid={`admin-docs-${a.application_id}`}>Docs</Button>
                    <Button size="sm" variant="outline" onClick={() => openChange(a)} data-testid={`admin-change-${a.application_id}`}>Status</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {apps.length === 0 && <Card className="p-6 text-center text-sm text-slate-500">No applications yet.</Card>}
        {apps.map((a) => (
          <Card
            key={a.application_id}
            onClick={() => openDetail(a.application_id)}
            className="cursor-pointer p-4"
            data-testid={`admin-app-m-${a.application_id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{a.borrower_name}</div>
                <div className="truncate text-xs text-slate-500">{a.borrower_email}</div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
            </div>
            <div className="mt-2 truncate text-sm text-slate-600">{a.property_address}</div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusBadge status={a.status} />
              <AiScoreBadge score={a.ai_score} />
              <span className="text-xs text-slate-500">{LOAN_TYPE_LABELS[a.loan_type] || a.loan_type}</span>
            </div>
            <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => requestDocs(a.application_id)}><FileText className="mr-1 h-4 w-4" />Docs</Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openChange(a)}>Status</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-status-dialog">
          <DialogHeader>
            <DialogTitle>Change Application Status</DialogTitle>
            <DialogDescription>{selected?.borrower_name} · {selected?.property_address}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="admin-new-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 min-h-[90px]" data-testid="admin-status-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="admin-cancel">Cancel</Button>
            <Button onClick={save} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="admin-save-status">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
